import { ServiceBrand, ServiceLogo } from "./ServiceBrand";
import { serviceBrand, serviceDescription, serviceName } from "../../lib/serviceBranding";
import { Fragment } from "react";
import { Link } from "react-router-dom";
import { GithubMark, XMark } from "../BrandIcons";
import { formatBoon, formatRelative, formatUsdc, shortAddr } from "../../lib/api";
import { type X402LinkedAccount, type X402Route } from "../../lib/x402Discovery";

/* Hallmark · component: discovery card · genre: playful · theme: Boon warm paper
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass
 */
export function X402RouteRow({ route, compact = false }: { route: X402Route; compact?: boolean }) {
  const context = route.contexts[0];
  const review = route.reviews.find((item) => item.evidenceKind === "boon_backed")
    ?? route.reviews.find((item) => item.evidenceKind === "receipt_verified")
    ?? route.reviews.find((item) => item.evidenceKind === "self_reported");
  const description = route.description;
  const endpoint = description?.route ?? context?.note.route ?? route.endpointUrl ?? undefined;
  const title = readableTitle(description?.summary) ?? readableTitle(context?.note.intent) ?? (endpoint ? capabilityTitle(endpoint) : null) ?? "Service details pending";

  if (!compact) {
    return (
      <article className="group relative min-w-0 overflow-hidden rounded-xl border border-faint bg-paper p-5 transition-[border-color,transform] duration-150 hover:-translate-y-0.5 hover:border-olive focus-within:border-olive md:p-6">
        <div className="min-w-0">
          <span className={`absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-xl md:right-6 md:top-6 ${endpoint ? "bg-paper" : routeTone(title)}`} aria-hidden="true">
            {endpoint ? <ServiceLogo endpoint={endpoint.publicEndpointUrl} /> : <RouteUseGlyph title={title} className="h-5 w-5" />}
          </span>

          <div className="min-w-0 pr-14 md:pr-16">
            <div className="flex flex-wrap items-center gap-2">
              {endpoint && <span className="pill pill-faint max-w-full truncate">{serviceBrand(endpoint.publicEndpointUrl).name}</span>}
              {!endpoint && <span className="pill pill-faint">details pending</span>}
              {description?.price.display && <span className="pill pill-olive btn-mono text-[10px]">{description.price.display} per call</span>}
            </div>
            <h3 className="mt-3 min-w-0 break-words [overflow-wrap:anywhere] text-xl font-display tracking-tight text-ink md:text-2xl">
              <Link to={`/x402/routes/${route.routeId}`} className="hover:text-olive-deep hover:underline underline-offset-4">
                {title}
              </Link>
            </h3>

            {review ? (
              <div className="mt-3 flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted">
                <span>{reviewAttributionLabel(review.evidenceKind)}</span>
                <IdentityLink accounts={review.reviewer.accounts} wallet={review.reviewer.wallet} walletHref={`/x402/reviewers/${review.reviewer.wallet}`} />
              </div>
            ) : context ? (
              <div className="mt-3 flex min-w-0 flex-wrap items-center gap-2 text-xs text-muted">
                <span>Notes from</span>
                <IdentityLink accounts={context.publisherAccounts} wallet={context.publisher} walletHref={`/s/${context.publisher}`} />
              </div>
            ) : null}

            {review?.reviewText ? (
              <blockquote className="mt-4 max-w-2xl break-words [overflow-wrap:anywhere] text-sm text-ink-soft leading-relaxed">“{review.reviewText}”</blockquote>
            ) : context ? (
              <p className="mt-4 max-w-2xl text-sm text-ink-soft leading-relaxed line-clamp-2">{context.note.fit}</p>
            ) : (
              <p className="mt-4 text-sm text-muted">A participant has not published a plain-language description yet.</p>
            )}

            {endpoint && (
              <details className="mt-4 text-sm text-muted">
                <summary className="cursor-pointer min-h-9 inline-flex items-center gap-2 btn-mono text-[10px] text-olive-deep"><CodeGlyph className="h-3.5 w-3.5" /> Technical details</summary>
                <p className="mt-1 flex items-start gap-2 btn-mono text-xs break-all">
                  <span><span className="text-olive-deep">{endpoint.method}</span> {endpoint.publicEndpointUrl}</span>
                </p>
              </details>
            )}
          </div>

          <div className="mt-4 min-w-0">
            <Link className="btn btn-ghost max-w-full whitespace-nowrap" to={`/x402/routes/${route.routeId}`}>
              {endpoint ? "View tool" : "View record"} →
            </Link>
            <p className="mt-2 btn-mono text-[10px] text-muted">
              {route.recognition.routedBoonCount} public recognition{route.recognition.routedBoonCount === 1 ? "" : "s"} · {formatRelative(route.recognition.latestRecognitionAt)}
            </p>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="min-w-0 overflow-hidden bg-paper px-4 py-4">
      <div className="grid gap-4 md:grid-cols-[minmax(0,1fr)_auto] md:items-start">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {endpoint && <ServiceBrand endpoint={endpoint.publicEndpointUrl} compact />}
            {!endpoint && <span className="pill pill-faint">not discoverable yet</span>}
            {description && <span className="pill pill-faint">{description.service}</span>}
            {context && <span className="pill pill-faint">participant note</span>}
          </div>
          <h3 className="mt-2 text-lg font-display tracking-tight text-ink">
            <Link to={`/x402/routes/${route.routeId}`} className="hover:underline underline-offset-4">{title}</Link>
          </h3>
          {endpoint ? (
            <>
              <details className="mt-2 text-sm text-muted">
                <summary className="cursor-pointer min-h-9 inline-flex items-center btn-mono text-[10px] text-olive-deep">Technical details</summary>
                <p className="mt-1 btn-mono text-xs break-all"><span className="text-olive-deep">{endpoint.method}</span> {endpoint.publicEndpointUrl}</p>
              </details>
              {review ? <p className="mt-2 text-xs text-muted btn-mono">{evidenceLabel(review.evidenceKind)} by <IdentityLink accounts={review.reviewer.accounts} wallet={review.reviewer.wallet} walletHref={`/x402/reviewers/${review.reviewer.wallet}`} /></p>
                : context ? <p className="mt-2 text-xs text-muted btn-mono">note by <IdentityLink accounts={context.publisherAccounts} wallet={context.publisher} walletHref={`/s/${context.publisher}`} /></p>
                  : null}
            </>
          ) : <p className="mt-2 text-sm text-muted">A participant has not published endpoint details yet.</p>}
        </div>
        <dl className="grid grid-cols-3 gap-x-6 gap-y-3 md:min-w-36 md:grid-cols-1">
          <Fact label="USDC recognized" value={formatUsdc(route.recognition.totalRecognitionUsdc)} />
          <Fact label="$BOON burned" value={formatBoon(route.recognition.boonBurned)} />
          <Fact label="Last recognized" value={formatRelative(route.recognition.latestRecognitionAt)} />
        </dl>
      </div>
    </article>
  );
}

function readableTitle(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  if (!trimmed || /^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)\b|https?:\/\//i.test(trimmed)) return null;
  return trimmed;
}

function capabilityTitle(endpoint: { method: string; publicEndpointUrl: string }): string {
  const knownDescription = serviceDescription(endpoint.publicEndpointUrl, endpoint.method);
  if (knownDescription) return serviceName(endpoint.publicEndpointUrl, endpoint.method, knownDescription);
  return serviceBrand(endpoint.publicEndpointUrl).name;
}

function evidenceLabel(kind: X402Route["reviews"][number]["evidenceKind"]): string {
  if (kind === "boon_backed") return "Boon-backed review";
  if (kind === "receipt_verified") return "receipt-verified use review";
  return "self-reported review";
}

function reviewAttributionLabel(kind: X402Route["reviews"][number]["evidenceKind"]): string {
  if (kind === "boon_backed") return "Boon-backed review by";
  if (kind === "receipt_verified") return "Receipt-verified use review by";
  return "Self-reported review by";
}

function IdentityLink({ accounts, wallet, walletHref }: { accounts: X402LinkedAccount[]; wallet: string; walletHref: string }) {
  if (accounts.length > 0) {
    return <>{accounts.map((account, index) => (
      <Fragment key={account.handle}>
        {index > 0 && <span className="text-muted">+</span>}
        <Link className="inline-flex min-h-7 max-w-full min-w-0 items-center gap-1.5 rounded-full border border-faint bg-paper-deep/45 px-2 text-olive-deep hover:border-olive hover:underline" to={`/p/${encodeURIComponent(account.handle)}`} title={account.handle}>
          <ProviderIcon provider={account.provider} />
          <span className="max-w-48 truncate">{account.handle}</span>
        </Link>
      </Fragment>
    ))}</>;
  }
  return (
    <Link className="inline-flex min-h-7 max-w-full min-w-0 items-center gap-1.5 rounded-full border border-faint bg-paper-deep/45 px-2 text-olive-deep hover:border-olive hover:underline" to={walletHref} title={wallet}>
      <PersonGlyph className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{shortAddr(wallet)}</span>
    </Link>
  );
}

function ProviderIcon({ provider }: { provider: X402LinkedAccount["provider"] }) {
  if (provider === "x") return <XMark className="h-3 w-3" />;
  if (provider === "github") return <GithubMark className="h-3.5 w-3.5" />;
  return <RobotGlyph className="h-3.5 w-3.5" />;
}

function routeTone(title: string): string {
  const text = title.toLowerCase();
  if (/property|parcel|local/.test(text)) return "bg-clay-soft text-clay-deep";
  if (/search|research|knowledge/.test(text)) return "bg-amber-soft text-amber-deep";
  return "bg-olive-soft text-olive-deep";
}

function RouteUseGlyph({ title, className }: { title: string; className?: string }) {
  const text = title.toLowerCase();
  if (/property|parcel|local/.test(text)) return <MapGlyph className={className} />;
  if (/search|research|knowledge/.test(text)) return <SearchGlyph className={className} />;
  return <GlobeGlyph className={className} />;
}

function Fact({ label, value }: { label: string; value: string }) {
  return <div><dt className="btn-mono text-[10px] uppercase tracking-[0.14em] text-muted">{label}</dt><dd className="mt-1 text-sm text-ink num">{value}</dd></div>;
}

type GlyphProps = { className?: string };
function SearchGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="10.5" cy="10.5" r="5.5"/><path d="m15 15 5 5"/></svg>; }
function MapGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m4 6 5-2 6 2 5-2v14l-5 2-6-2-5 2zM9 4v14M15 6v14"/></svg>; }
function GlobeGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18"/></svg>; }
function CodeGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="m8 7-5 5 5 5M16 7l5 5-5 5M14 4l-4 16"/></svg>; }
function PersonGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><circle cx="12" cy="8" r="3.25"/><path d="M5.5 20c.7-4 3-6 6.5-6s5.8 2 6.5 6"/></svg>; }
function RobotGlyph({ className }: GlyphProps) { return <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M12 3v3M8 3h8M5 9h14v10H5z"/><circle cx="9" cy="13" r="1" fill="currentColor" stroke="none"/><circle cx="15" cy="13" r="1" fill="currentColor" stroke="none"/><path d="M9 17h6"/></svg>; }
